<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  RrInput,
  RrButton,
  RrForm,
  RrFormItem,
  RrSpace,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type { CreateParamRequest, SystemParam, UpdateParamRequest } from '../../types'

type DraftMode = 'create' | 'edit'

interface ParamDraft {
  mode: DraftMode
  originalCode: string
  code: string
  value: string
  remark: string
}

const props = defineProps<{
  params: SystemParam[]
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateParamRequest]
  update: [code: string, payload: UpdateParamRequest]
  delete: [code: string]
}>()

const showDraftForm = ref(false)
const filters = ref({
  keyword: ''
})
const draft = ref<ParamDraft>(emptyDraft())

const columns: TableColumnType<SystemParam>[] = [
  { title: '编码', dataIndex: 'code', key: 'code' },
  { title: '值', dataIndex: 'value', key: 'value' },
  { title: '备注', dataIndex: 'remark', key: 'remark' },
  { title: '操作', key: 'action', width: 150 }
]

const page = ref(1)
const pageSize = ref(20)

const filteredParams = computed(() => {
  const keyword = filters.value.keyword.trim().toLowerCase()
  return props.params.filter((param) => {
    if (!keyword) return true
    return (
      param.code.toLowerCase().includes(keyword) ||
      param.value.toLowerCase().includes(keyword) ||
      param.remark.toLowerCase().includes(keyword)
    )
  })
})

const paginatedParams = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredParams.value.slice(start, start + pageSize.value)
})

watch(() => filters.value.keyword, () => {
  page.value = 1
})

function emptyDraft(): ParamDraft {
  return {
    mode: 'create',
    originalCode: '',
    code: 'site.theme',
    value: 'default',
    remark: ''
  }
}

function openCreateForm() {
  draft.value = emptyDraft()
  showDraftForm.value = true
}

function openEditForm(param: SystemParam) {
  draft.value = {
    mode: 'edit',
    originalCode: param.code,
    code: param.code,
    value: param.value,
    remark: param.remark
  }
  showDraftForm.value = true
}

function closeDraftForm() {
  showDraftForm.value = false
  draft.value = emptyDraft()
}

function resetFilters() {
  filters.value = { keyword: '' }
}

function submitDraft() {
  if (!draft.value.code.trim()) return
  const payload = {
    value: draft.value.value.trim(),
    remark: draft.value.remark.trim()
  }
  if (draft.value.mode === 'create') {
    if (!props.canCreate) return
    emit('create', {
      code: draft.value.code.trim(),
      ...payload
    })
  } else {
    if (!props.canUpdate) return
    emit('update', draft.value.originalCode, payload)
  }
  closeDraftForm()
}

function deleteParam(param: SystemParam) {
  if (!props.canDelete || props.loading) return
  emit('delete', param.code)
}

function formatTime(value: string) {
  if (!value) return ''
  return value.replace('T', ' ').replace(/\.\d+Z?$/, '')
}
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Parameter Management</p>
        <h2>参数管理</h2>
      </div>
      <span>{{ filteredParams.length }} / {{ params.length }} 个参数</span>
    </div>

    <AdminFilterBar
      :show-search="false"
      :show-reset="false"
      :expandable="false"
    >
      <label>
        编码
        <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="编码、值、备注" />
      </label>
      <template #actions>
        <RrButton :disabled="loading" @click="resetFilters">查询</RrButton>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="openCreateForm">新增</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      :open="showDraftForm"
      :title="draft.mode === 'create' ? '新增参数' : '修改参数'"
      :width="500"
      @close="closeDraftForm"
    >
      <template #extra>
        <span>{{ draft.mode === 'create' ? (canCreate ? '可写' : '只读') : (canUpdate ? '可写' : '只读') }}</span>
      </template>
      <RrForm
        layout="vertical"
        :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
        @submit.prevent="submitDraft"
      >
        <RrFormItem label="编码" required>
          <RrInput v-model:value.trim="draft.code" autocomplete="off" :disabled="draft.mode === 'edit'" />
        </RrFormItem>
        <RrFormItem label="值" required>
          <RrInput v-model:value.trim="draft.value" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="备注">
          <RrInput v-model:value.trim="draft.remark" autocomplete="off" />
        </RrFormItem>
        <RrSpace style="margin-top: 16px">
          <RrButton
            type="primary"
            html-type="submit"
            :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
          >
            {{ draft.mode === 'create' ? '创建参数' : '保存参数' }}
          </RrButton>
          <RrButton :disabled="loading" @click="closeDraftForm">取消</RrButton>
        </RrSpace>
      </RrForm>
    </AdminDrawer>

    <AdminDataTable
      :columns="columns"
      :data-source="paginatedParams"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="filteredParams.length"
      row-key="code"
      :count="filteredParams.length"
      :empty="filteredParams.length === 0"
      empty-description="暂无匹配参数。"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'code'">
          <span>
            <strong>{{ record.code }}</strong>
            <small style="display: block; color: var(--rr-text-secondary); font-size: 12px">{{ formatTime(record.updatedAt) }}</small>
          </span>
        </template>
        <template v-else-if="column.key === 'value'">
          {{ record.value || '-' }}
        </template>
        <template v-else-if="column.key === 'remark'">
          {{ record.remark || '-' }}
        </template>
        <template v-else-if="column.key === 'action'">
          <RrSpace size="small">
            <RrButton type="link" :disabled="!canUpdate || loading" @click="openEditForm(record)">
              修改
            </RrButton>
            <RrButton type="link" danger :disabled="!canDelete || loading" @click="deleteParam(record)">
              删除
            </RrButton>
          </RrSpace>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
